import React, { useCallback } from "react";
import { EdgeProps, getBezierPath } from "reactflow";

const CustomEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  selected,
  data,
}) => {
  // Check if this is a decision edge (has _true or _false in the ID)
  const isDecisionEdge = id.includes("_true") || id.includes("_false");
  
  // Slightly extend lines that target end nodes so they visually meet the top handle
  const targetYOffset = (data as any)?.toDestination ? -4 : 0;

  // Check if this edge is pointing upward (target is above source)
  const isUpwardEdge = targetY < sourceY;
  
  let edgePath: string;
  
  if (isUpwardEdge && Math.abs(targetX - sourceX) < 50) {
    // For upward edges that are nearly vertical, create a rectangular loop with rounded corners
    const loopOffset = 150; // How far to the side the loop extends
    const dropDistance = 30; // Reduced: How far down from source before going sideways
    const riseDistance = 30; // How far up from target before going sideways
    const radius = Math.min(dropDistance, riseDistance); // Corner radius for smooth turns
    
    // Always loop to the right for consistency
    const sideX = Math.max(sourceX, targetX) + loopOffset;
    
    // Create a path with smooth corners using quadratic bezier curves
    edgePath = `
      M ${sourceX} ${sourceY}
      L ${sourceX} ${sourceY + dropDistance - radius}
      Q ${sourceX} ${sourceY + dropDistance}, ${sourceX + radius} ${sourceY + dropDistance}
      L ${sideX - radius} ${sourceY + dropDistance}
      Q ${sideX} ${sourceY + dropDistance}, ${sideX} ${sourceY + dropDistance - radius}
      L ${sideX} ${targetY - riseDistance + radius}
      Q ${sideX} ${targetY - riseDistance}, ${sideX - radius} ${targetY - riseDistance}
      L ${targetX + radius} ${targetY - riseDistance}
      Q ${targetX} ${targetY - riseDistance}, ${targetX} ${targetY - riseDistance + radius}
      L ${targetX} ${targetY + targetYOffset}
    `;
  } else {
    // Use the standard bezier path for other cases
    [edgePath] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY: targetY + targetYOffset,
      targetPosition,
    });
  }

  // Respect provided style (stroke, strokeWidth, strokeDasharray). Fallbacks if missing.
  const fallbackStroke = isDecisionEdge ? "#b1b1b7" : "#b1b1b7";
  const fallbackWidth = isDecisionEdge ? 2 : 1;

  const effectiveStroke = (style.stroke as string) || fallbackStroke;

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (data && typeof (data as any).onContextMenu === "function") {
      data?.onContextMenu?.(id, e.clientX, e.clientY);
    }
  }, [data, id]);

  return (
    <>
      {selected && (
        <path
          id={`${id}-highlight`}
          style={{
            stroke: effectiveStroke,
            strokeWidth: (style.strokeWidth as number || fallbackWidth) + 3,
            opacity: 0.6,
            pointerEvents: "none",
          }}
          className="react-flow__edge-path"
          d={edgePath}
        />
      )}
      <path
        id={id}
        style={{
          stroke: fallbackStroke,
          strokeWidth: fallbackWidth,
          // Allow edge-provided styles to override fallbacks (important for upstream styling)
          ...style,
        }}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
        onContextMenu={handleContextMenu}
      />
      {/* Invisible wide interaction path to improve click/touch hit area */}
      <path
        d={edgePath}
        className="react-flow__edge-interaction"
        style={{
          fill: "none",
          stroke: "transparent",
          strokeWidth: Math.max((style.strokeWidth as number) || fallbackWidth, 12),
          cursor: "pointer",
        }}
        onContextMenu={handleContextMenu}
      />
    </>
  );
};

export default CustomEdge; 